import { useState } from "react";
import GlowingEmailInput from "@/components/GlowingEmailInput";
import { WaitlistJoinedMessage } from "@/components/WaitlistJoinedMessage";
import { useWaitlist } from "@/context/WaitlistContext";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function FinalCTASection({ api }) {
  const { joined, markJoined } = useWaitlist();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${api}/waitlist`, { email, source: "footer" });
      markJoined(email);
      setEmail("");
      toast.success("You are on the waitlist.");
    } catch (err) {
      if (err.response?.status === 409) {
        markJoined(email);
        toast.info("You are already on the waitlist.");
      } else {
        toast.error("Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="final-cta"
      data-testid="final-cta-section"
      className="relative py-14 sm:py-[4.25rem] overflow-x-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/a964e757-17d0-42c6-9ae1-72dac48014e0/images/e2e0bb95aa7a68ec8c16a56ac92ef671160cfbc23685c9ccd75435e4572a366c.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[hsl(270_24%_5%/0.78)]" />
      </div>

      {/* Ambient glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(90vw,420px)] h-[240px] bg-fuchsia-600/[0.06] rounded-full blur-[90px] pointer-events-none" />

      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        <div className="reveal">
          <h2
            data-testid="final-cta-headline"
            className="inline-block text-4xl sm:text-5xl tracking-tighter font-bold leading-[1.2] pb-1.5 mb-5 bg-gradient-to-r from-white via-violet-200 to-fuchsia-200 bg-clip-text text-transparent"
          >
            Stop missing out
          </h2>
          {!joined && (
            <p className="text-base text-violet-200/45 mb-8 max-w-md mx-auto">
              Join the waitlist for early access. We&apos;ll email you when your spot opens.
            </p>
          )}

          {!joined ? (
            <form
              data-testid="final-cta-form"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-lg mx-auto w-full"
              noValidate
            >
              <GlowingEmailInput
                data-testid="final-cta-email-input"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@company.com"
                aria-label="Email address for waitlist"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="flex-1 min-w-0 w-full"
              />
              <button
                data-testid="final-cta-submit-button"
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="h-[52px] shrink-0 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white font-medium rounded-full px-6 shadow-lg shadow-violet-600/25 hover:from-violet-400 hover:to-fuchsia-500 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap w-full sm:w-auto justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {loading ? "Joining..." : "Get Early Access"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <WaitlistJoinedMessage
              data-testid="final-cta-success"
              className="border-violet-400/25 bg-violet-500/[0.1]"
            />
          )}
        </div>
      </div>
    </section>
  );
}
